import { isEqual } from "lodash";
import uniqueId from "lodash/uniqueId";
import * as React from "react";
import { JSX } from "react";
import { Link } from "react-router-dom";
import { ExportProviderAvailabilityType, ExportType } from "@app/exports/enumerations";
import { ExportDataRouteState } from "@app/exports/exportsRouteState";
import { CountyProjectContextProps, withCountyProjectContext } from "@app/projects/CountyProjectContext";
import {
  LocationConfidenceScoresLabelEnum,
  NetworkAssessmentAnalysisType,
  ProjectionProviderType,
  TimeFrequency
} from "@app/projects/enumerators";
import { getPrimaryAndSecondaryRunConfigurations } from "@app/projects/network-assessment/adequacy/utility";
import { DisplayOptions } from "@app/projects/network-assessment/DisplayOptions";
import {
  ClaimsActivityLineOfBusinessTypeEnum,
  ClaimsActivityVolumeLabelEnum
} from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import {
  DeleteProjectionProvider,
  ProjectionPanelMode,
  ProjectionProviderDetails
} from "@app/projects/projection-provider";
import {
  ProjectionProviderDetailsActionType,
  ProjectionProviderDetailsProps,
  withProjectionProviderDetailsReducer
} from "@app/projects/projection-provider/projectionProviderDetailsReducer";
import { convertServicingProvidersToAccessPoints } from "@app/projects/projection-provider/utils";
import { ServicingProviderResult } from "@app/projects/results/servicing-provider";
import ServicingProvider from "@app/projects/results/servicing-provider/servicingProvider";
import {
  CareJourneyScoresValueEnum,
  EffectivenessEfficiencyValueEnum,
  MotiveScoreValueEnum,
  NewPatientClaimsEnum,
  TopCondition,
  TopProcedure
} from "@app/projects/servicing-provider";
import { dismiss, toast } from "@app/projects/servicing-provider/DistanceResultToast";
import {
  buildAdequacyProvidersColumnConfig,
  buildCommonImpactProviderColumns,
  getCargoDataForProvider,
  getEntityIcon
} from "@app/projects/servicing-provider/ServicingProviderHelpers";
import ServicingProviderProfile from "@app/projects/servicing-provider/ServicingProviderProfile";
import { ACCESS_POINT_PROFILE_FLYOUT_TITLE } from "@app/projects/servicing-provider/servicingProviderProfileConstants";
import { SimulateProviderTargeting } from "@app/projects/servicing-provider/SimulateProviderTargeting";
import {
  GainsPercentEnum,
  ImpactProviderTableData,
  ImpactProviderTableDataWithCargo,
  NPF
} from "@app/projects/servicing-provider/types";
import { ZipContextEventHandlersObject } from "@app/projects/zip-projects/interfaces";
import { ZipContextState } from "@app/projects/zip-projects/interfaces/zipContextState";
import { getSearchParametersFromFilterSelections } from "@app/projects/zip-projects/utility";
import { withZipContextEventHandlers, withZipContextState } from "@app/projects/zip-projects/ZipProjectContext";
import { RegionGranularity } from "@app/region-sets";
import {
  ReportCustomizationRouteState
} from "@app/report-customization/build-reports/interfaces/reportCustomizationRouteState";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { ResourceType } from "@app/resources";
import { Flyout, FlyoutContextOutgoingProps, FlyoutLink, withFlyoutContext } from "@app/shared/Flyouts";
import FlyoutContextProvider from "@app/shared/Flyouts/FlyoutContext";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { getProviderGapStatusDecoratorWithFlags } from "@app/shared/providers/utils";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import GeographyService from "@app/geography/geographyService";
import ProjectService from "@app/projects/projectService";
import AccessSummary from "@app/projects/results/adequacy/accessSummary";
import ProjectionProvider from "@app/projects/results/projection-provider/projectionProvider";
import GainsPercentFilter from "@app/projects/servicing-provider/provider-claims/GainsPercentFilter";
import ConfidenceTierSearchFilters from "@app/projects/servicing-provider/provider-claims/ConfidenceTierSearchFilters";
import ProviderClaimsFilters from "@app/projects/servicing-provider/provider-claims/ProviderClaimsFilters";
import { SpecialtyPredictionModelEnum } from "@app/projects/servicing-provider/provider-claims/SpecialtyPredictionModelEnum";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { formatDateCustom } from "@app/shared/formatDateTime";
import { QESButton } from "@app/shared/qes-standards/Button";
import { SearchBox } from "@app/shared/SearchBox";
import { Toggle } from "@app/shared/Toggle";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { capitalizeFirstLetter } from "@app/shared/utility/capitalizeFirstLetter";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContextProps, withUserContext } from "@app/user/UserContext";
import { restrictByFields } from "@app/viewable-fields/utilities";
import { TableType, ViewableField } from "@app/viewable-fields/viewableFields";
import { ViewableFieldsContextProps, withViewableFields } from "@app/viewable-fields/ViewableFieldsContext";

type ImpactProvidersPanelProps = ProjectionProviderDetailsProps & ZipContextState & CountyProjectContextProps
  & ViewableFieldsContextProps & UserContextProps & FeatureFlagContextProps & FlyoutContextOutgoingProps &
  ZipContextEventHandlersObject & {
    accessSummary: AccessSummary;
    className?: string;
    exportEndpoint: string;
    projectURL: string;
    providerNetworkType: ProviderNetworkTypeEnum.Market | ProviderNetworkTypeEnum.Prospect;
    regionSetId: number;
    specialtyGroupCode: string;
  };

interface ImpactProvidersPanelState {
  columnConfig: ColumnConfig;
  disabledGainsFilter: boolean;
  displayOptionsDirty: boolean;
  displayedProviderType: DisplayedProviderType;
  displayedProviderTypeChanged: boolean;
  excludedRecords: ServicingProvider[];
  hasExcludes: boolean;
  hasServicing: boolean;
  hasTargets: boolean;
  isOpen: boolean;
  lastSearchTerm: string;
  selectedProjectionProvider: ProjectionProvider;
  selectedProjectionProviderType: ProjectionProviderType;
  selectedCanEdit: boolean;
  editFlyoutTitle: string;
  selectedFlyoutButtonRef: number;
  lastSelectedCareJourneyCostValues: CareJourneyScoresValueEnum[];
  lastSelectedCareJourneyQualityValues: CareJourneyScoresValueEnum[];
  lastSelectedConditions: string[];
  lastSelectedEffectivenessValues: EffectivenessEfficiencyValueEnum[];
  lastSelectedEfficiencyValues: EffectivenessEfficiencyValueEnum[];
  lastSelectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
  lastSelectedVolumeLabels: ClaimsActivityVolumeLabelEnum[];
  lastSelectedLocationConfidenceValues: LocationConfidenceScoresLabelEnum[];
  lastSelectedMotiveAppropriatenessValues: MotiveScoreValueEnum[];
  lastSelectedNewPatientClaimsValues: NewPatientClaimsEnum[];
  lastSelectedPgrConfidenceTiers: number[];
  lastSelectedProcedures: string[];
  lastSelectedSpecialtyPredictionModelFilter?: SpecialtyPredictionModelEnum;
  lastSortedCargo: string;
  lastSortedColumn: keyof ImpactProviderTableDataWithCargo;
  lastSortedDirection: SortDirections;
  loadStatus: LoadStatus;
  offset: number;
  gainsPercent: GainsPercentEnum;
  sortedRecords: ServicingProvider[];
  selectedProviders: ServicingProvider[];
  tableData: ImpactProviderTableDataWithCargo[];
  tableDataExcluded: ImpactProviderTableDataWithCargo[];
  tableDataTargeted: ImpactProviderTableDataWithCargo[];
  targetedRecords: ServicingProvider[];
  topConditions: DropdownItem<TopCondition>[];
  topProcedures: DropdownItem<TopProcedure>[];
  totalCount: number;
  providers: ServicingProvider[];
}

type LoadStatus = "empty" | "error" | "loaded" | "loading" | "partialLoading" | "shouldLoad";

type DisplayedProviderType = "excluded" | "servicing" | "targeted";

// Impact Provider Fields
// Abbreviated to make repeated references shorter.
// needs to match ImpactServicingProviderModel.cs (NPF handles everything from NetworkServicingProviderModel.cs)
//enum IPF {
//  GainsPercent = "GainsPercent",
//  ImpactsPercent = "ImpactsPercent",
//}

class ImpactProvidersPanel extends React.Component<ImpactProvidersPanelProps, ImpactProvidersPanelState> {
  private readonly batchCount: number = 100;
  private readonly geoService = new GeographyService();
  private readonly projectService = new ProjectService();
  private readonly title: string;
  private readonly tableType: TableType;
  private readonly toastId = uniqueId("distance-toast-");
  private cargoColumns = new Set<string>();
  private providerPlurality = "providers";
  private isZipProject: boolean;
  // Guards against a slower, superseded request resolving after a newer one and overwriting its results.
  private latestRequestSequence: number = 0;
  private flyoutButtonRefs: React.RefObject<React.RefObject<HTMLButtonElement>[]>;

  constructor(props: ImpactProvidersPanelProps) {
    super(props);
    this.flyoutButtonRefs = React.createRef<React.RefObject<HTMLButtonElement>[]>();
    this.flyoutButtonRefs.current = [];
    this.title = this.props.providerNetworkType === ProviderNetworkTypeEnum.Market ? "Market Servicing Providers" : "Prospect Servicing Providers";
    this.tableType = this.props.providerNetworkType === ProviderNetworkTypeEnum.Market ? TableType.MarketProviders : TableType.ProspectProviders;
    this.state = {
      columnConfig: {},
      disabledGainsFilter: false,
      displayedProviderType: "servicing",
      displayedProviderTypeChanged: false,
      displayOptionsDirty: false,
      editFlyoutTitle: "",
      excludedRecords: [],
      gainsPercent: GainsPercentEnum.All,
      hasExcludes: false,
      hasServicing: false,
      hasTargets: false,
      isOpen: false,
      lastSearchTerm: "",
      lastSelectedCareJourneyCostValues: undefined,
      lastSelectedCareJourneyQualityValues: undefined,
      lastSelectedConditions: [],
      lastSelectedEffectivenessValues: undefined,
      lastSelectedEfficiencyValues: undefined,
      lastSelectedLineOfBusiness: undefined,
      lastSelectedLocationConfidenceValues: undefined,
      lastSelectedMotiveAppropriatenessValues: undefined,
      lastSelectedNewPatientClaimsValues: undefined,
      lastSelectedPgrConfidenceTiers: [],
      lastSelectedProcedures: [],
      lastSelectedVolumeLabels: undefined,
      lastSortedCargo: undefined,
      lastSortedColumn: undefined,
      lastSortedDirection: undefined,
      loadStatus: "shouldLoad",
      offset: 0,
      providers: [],
      selectedCanEdit: false,
      selectedFlyoutButtonRef: 0,
      selectedProjectionProvider: undefined,
      selectedProjectionProviderType: undefined,
      selectedProviders: [],
      sortedRecords: [],
      tableData: [],
      tableDataExcluded: [],
      tableDataTargeted: [],
      targetedRecords: [],
      topConditions: [],
      topProcedures: [],
      totalCount: 0,
    };
    this.isZipProject = this.props.project.resource.resourceType === ResourceType.ZipAnalysisProject;
  }

  public async componentDidMount() {
    // this is async and probably won't have anything in time for this setState
    //this.props.getByTableType(this.tableType);
    const viewableFields = this.props.viewableFieldsByTableType.get(this.tableType);
    const newColumnConfig = this.getColumnConfig(viewableFields);
    this.setState({
      columnConfig: newColumnConfig
    });
  }

  public async componentDidUpdate(prevProps: ImpactProvidersPanelProps, prevState: ImpactProvidersPanelState) {
    if (prevState.loadStatus === "loaded" && this.state.loadStatus === "shouldLoad") {
      await this.buildServicingProviders(true);
    }

    if (this.props.shouldRefreshProspectProviders && this.props.providerNetworkType === ProviderNetworkTypeEnum.Prospect) {
      this.props.setShouldRefreshProspectProviders(false);
      // A zip/specialty filter change means any search/filter state left over from the
      // previous selection no longer applies to the new provider set, so clear it before refreshing.
      this.setState({
        displayedProviderTypeChanged: true,
        lastSearchTerm: "",
        lastSelectedCareJourneyCostValues: undefined,
        lastSelectedCareJourneyQualityValues: undefined,
        lastSelectedConditions: [],
        lastSelectedEffectivenessValues: undefined,
        lastSelectedEfficiencyValues: undefined,
        lastSelectedLineOfBusiness: undefined,
        lastSelectedLocationConfidenceValues: undefined,
        lastSelectedMotiveAppropriatenessValues: undefined,
        lastSelectedNewPatientClaimsValues: undefined,
        lastSelectedProcedures: [],
        lastSelectedSpecialtyPredictionModelFilter: undefined,
        lastSelectedVolumeLabels: undefined,
      }, () => {
        this.buildServicingProviders(
          true, undefined, undefined, undefined, true,
          undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined,
          undefined, undefined, undefined, undefined, undefined, undefined, undefined,
          true,
        );
      });
    }

    if (this.props.shouldRefreshMarketProviders && this.props.providerNetworkType === ProviderNetworkTypeEnum.Market) {
      this.props.setShouldRefreshMarketProviders(false);
      this.setState({
        displayedProviderTypeChanged: true,
        lastSearchTerm: "",
        lastSelectedCareJourneyCostValues: undefined,
        lastSelectedCareJourneyQualityValues: undefined,
        lastSelectedConditions: [],
        lastSelectedEffectivenessValues: undefined,
        lastSelectedEfficiencyValues: undefined,
        lastSelectedLineOfBusiness: undefined,
        lastSelectedLocationConfidenceValues: undefined,
        lastSelectedMotiveAppropriatenessValues: undefined,
        lastSelectedNewPatientClaimsValues: undefined,
        lastSelectedProcedures: [],
        lastSelectedSpecialtyPredictionModelFilter: undefined,
        lastSelectedVolumeLabels: undefined,
      }, () => {
        this.buildServicingProviders(
          true, undefined, undefined, undefined, true,
          undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined,
          undefined, undefined, undefined, undefined, undefined, undefined, undefined,
          true,
        );
      });
    }

    if (this.state.displayedProviderType === "excluded" && !this.state.hasExcludes) {
      if (!this.state.hasExcludes) {
        if (this.state.sortedRecords.length) {
          this.setState({
            displayedProviderType: "servicing",
          });
        } else if (this.state.hasTargets) {
          this.setState({
            displayedProviderType: "targeted",
          });
        }
      }
    }

    if (this.state.displayedProviderType === "targeted" && !this.state.hasTargets) {
      if (!this.state.hasTargets) {
        if (this.state.sortedRecords.length) {
          this.setState({
            displayedProviderType: "servicing",
          });
        } else if (this.state.hasExcludes) {
          this.setState({
            displayedProviderType: "excluded",
          });
        }
      }
    }

    if (prevState.gainsPercent !== this.state.gainsPercent && !this.props.project.showPciData) {
      this.buildServicingProviders(true, this.state.lastSortedColumn, this.state.lastSortedDirection, this.state.lastSortedCargo, true);
    }

    const prevViewableFields = prevProps.viewableFieldsByTableType.get(this.tableType);
    const viewableFields = this.props.viewableFieldsByTableType.get(this.tableType);
    if (!isEqual(prevViewableFields, viewableFields) ||
      prevState.displayedProviderType !== this.state.displayedProviderType ||
      !isEqual(prevProps.runConfiguration, this.props.runConfiguration)) {
      const newColumnConfig = this.getColumnConfig();
      this.setState({
        columnConfig: newColumnConfig,
      });
    }
  }

  public componentWillUnmount() {
    dismiss(this.toastId);
  }

  public render() {
    const initialLoading = !this.state || (this.state.loadStatus === "loading" || this.state.loadStatus === "shouldLoad");
    const shouldShowBatchCount = !!this.state && (this.state.isOpen && (this.state.loadStatus === "loaded" || this.state.loadStatus === "partialLoading" || this.state.loadStatus === "empty"));
    const filterString = this.state.lastSearchTerm ? `, filtered to ${this.state.lastSearchTerm}` : "";
    const aliasString = this.tableType === TableType.MarketProviders
      ? !!this.props.runConfiguration.marketProviderSetAlias
        ? ` (${this.props.runConfiguration.marketProviderSetAlias})`
        : ""
      : this.tableType === TableType.ProspectProviders
        ? !!this.props.runConfiguration.prospectProviderSetAlias
          ? ` (${this.props.runConfiguration.prospectProviderSetAlias})`
          : ""
        : "";
    const title = this.state.displayedProviderType === "excluded"
      ? `${this.title}${aliasString}${shouldShowBatchCount ? ` (${Math.min(this.state.offset, this.state.excludedRecords.length)} of ${this.state.excludedRecords.length}${filterString})` : ""}`
      : this.state.displayedProviderType === "servicing"
        ? `${this.title}${aliasString}${shouldShowBatchCount ? ` (${this.state.sortedRecords.length} of ${this.state.totalCount}${filterString})` : ""}`
        : `${this.title}${aliasString}${shouldShowBatchCount ? ` (${Math.min(this.state.offset, this.state.targetedRecords.length)} of ${this.state.targetedRecords.length}${filterString})` : ""}`;
    const testId = this.tableType === TableType.MarketProviders ? "market-providers-panel" :
      this.tableType === TableType.ProspectProviders ? "prospect-providers-panel" :
        "";
    this.providerPlurality = this.state.selectedProviders.length === 1 ? "provider" : "providers";

    return (
      <>
        <AccordionContainer className={this.props.className}>
          <Accordion
            defaultOpen={false}
            title={title}
            onPanelWillOpen={initialLoading ? async () => await this.buildServicingProviders() : null}
            onPanelDidOpen={this.handleMainPanelDidOpen}
            onPanelDidClose={this.handleMainPanelDidClose}
            testId={testId}
          >
            {this.renderSearchField()}
            {this.renderProviderTable()}
          </Accordion>
        </AccordionContainer>
        <Flyout
          triggerElement={this.flyoutButtonRefs.current[this.state.selectedFlyoutButtonRef]}
          content={{
            body: <ProjectionProviderDetails
              {...this.props}
              accessPoints={convertServicingProvidersToAccessPoints(this.state.selectedProviders, this.props.providerNetworkType, this.props.specialtyGroupCode)}
              formState={this.props.formState}
              formDispatch={this.props.formDispatch}
              hasProvidersWithoutNPI={this.state.selectedProviders.some((provider) => provider.npi === undefined) || this.state.selectedProjectionProvider?.npi === undefined}
              networkAssessmentAnalysisType={this.props.runConfiguration.networkAssessmentAnalysisType}
              projectId={this.props.project.projectId}
              projectionPanelMode={this.state.selectedCanEdit ? ProjectionPanelMode.Edit : ProjectionPanelMode.View}
              projectionProvider={this.state.selectedProjectionProvider}
              projectionProviderType={this.state.selectedProjectionProviderType}
              providerNetworkType={this.props.providerNetworkType}
              onProjectionProviderUpdated={this.handleProjectionProviderCreatedOrUpdated}
              specialtyGroupCode={this.props.specialtyGroupCode}
              isZipProject={this.isZipProject}
              showAffiliationsInsights={this.props.project.showAffiliationsInsights}
              isRunning={this.props.project?.isRunning}
            />,
            title: this.state.editFlyoutTitle,
          }}
        />
      </>
    );
  }

  private buildServicingProviders = async (
    forceLoad?: boolean,
    selectedColumn?: keyof ImpactProviderTableDataWithCargo,
    sortDirection?: SortDirections,
    columnSubKey?: string,
    resetOffset?: boolean,
    searchValue?: string,
    gainsPercent: GainsPercentEnum = this.state.gainsPercent,
    selectedCareJourneyCostValues: CareJourneyScoresValueEnum[] = this.state.lastSelectedCareJourneyCostValues,
    selectedCareJourneyQualityValues: CareJourneyScoresValueEnum[] = this.state.lastSelectedCareJourneyQualityValues,
    selectedEffectivenessValues: EffectivenessEfficiencyValueEnum[] = this.state.lastSelectedEffectivenessValues,
    selectedEfficiencyValues: EffectivenessEfficiencyValueEnum[] = this.state.lastSelectedEfficiencyValues,
    selectedVolumeLabels: ClaimsActivityVolumeLabelEnum[] = this.state.lastSelectedVolumeLabels,
    selectedLineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum = this.state.lastSelectedLineOfBusiness,
    selectedLocationConfidenceValues: LocationConfidenceScoresLabelEnum[] = this.state.lastSelectedLocationConfidenceValues,
    selectedMotiveAppropriatenessValues: MotiveScoreValueEnum[] = this.state.lastSelectedMotiveAppropriatenessValues,
    selectedNewPatientClaimsValues: NewPatientClaimsEnum[] = this.state.lastSelectedNewPatientClaimsValues,
    selectedTopProcedures: string[] = this.state.lastSelectedProcedures,
    selectedTopConditions: string[] = this.state.lastSelectedConditions,
    selectedSpecialtyPredictionModelFilter: SpecialtyPredictionModelEnum = this.state.lastSelectedSpecialtyPredictionModelFilter,
    selectedPgrConfidenceTiers: number[] = this.state.lastSelectedPgrConfidenceTiers,
    shouldRefreshServicingList = false,
  ) => {
    // Guards against a slower, superseded request resolving after a newer one and overwriting its results.
    let requestSequence: number;
    try {
      const nextLoadStatus: LoadStatus = this.state.loadStatus === "shouldLoad" || forceLoad
        ? "loading"
        : (this.state.loadStatus === "loaded" && this.state.offset < this.state.totalCount)
          ? "partialLoading"
          : null;

      const searchString = searchValue === undefined ? this.state.lastSearchTerm : searchValue;

      if (nextLoadStatus) {
        this.setState((prevState) => ({
          ...prevState,
          loadStatus: nextLoadStatus,
        }));

        requestSequence = ++this.latestRequestSequence;

        let excludedRecords: ServicingProvider[] = [...this.state.excludedRecords];
        let targetedRecords: ServicingProvider[] = [...this.state.targetedRecords];

        let offset = this.state.offset;
        if ((selectedColumn && selectedColumn !== this.state.lastSortedColumn) || (sortDirection !== undefined && sortDirection !== this.state.lastSortedDirection) || resetOffset) {
          offset = 0;
        }

        if (this.props.project.showPciData &&
          selectedLineOfBusiness === undefined &&
          selectedVolumeLabels === undefined &&
          selectedCareJourneyCostValues === undefined &&
          selectedCareJourneyQualityValues === undefined &&
          selectedEffectivenessValues === undefined &&
          selectedEfficiencyValues === undefined &&
          selectedLocationConfidenceValues === undefined &&
          selectedMotiveAppropriatenessValues === undefined &&
          selectedNewPatientClaimsValues === undefined
        ) {
          // pca-specific defaults for on load
          selectedCareJourneyCostValues = Object.values(CareJourneyScoresValueEnum);
          selectedCareJourneyQualityValues = Object.values(CareJourneyScoresValueEnum);
          selectedEffectivenessValues = Object.values(EffectivenessEfficiencyValueEnum);
          selectedEfficiencyValues = Object.values(EffectivenessEfficiencyValueEnum);
          selectedLineOfBusiness = ClaimsActivityLineOfBusinessTypeEnum.Total;
          selectedVolumeLabels = Object.values(ClaimsActivityVolumeLabelEnum).filter(x => x !== ClaimsActivityVolumeLabelEnum.Total);
          selectedLocationConfidenceValues = Object.values(LocationConfidenceScoresLabelEnum);
          selectedMotiveAppropriatenessValues = Object.values(MotiveScoreValueEnum);
          selectedNewPatientClaimsValues = Object.values(NewPatientClaimsEnum);
        }

        if (this.state.displayedProviderType === "excluded" || this.state.displayedProviderType === "targeted") {
          const networkAssessmentAnalysisType = this.isZipProject ? NetworkAssessmentAnalysisType.ServiceArea : this.props.runConfiguration.networkAssessmentAnalysisType;
          const projectionProvidersByType = await this.projectService.getImpactProjectionProviders(
            this.props.project.projectId,
            this.props.regionSetId,
            this.props.runConfiguration.projectRunConfigurationId,
            this.props.specialtyGroupCode,
            this.props.providerNetworkType,
            networkAssessmentAnalysisType,
            gainsPercent,
            selectedColumn as string,
            sortDirection,
            columnSubKey,
            searchString,
            selectedEffectivenessValues,
            selectedEfficiencyValues,
            selectedVolumeLabels,
            selectedLineOfBusiness,
            selectedLocationConfidenceValues,
            selectedMotiveAppropriatenessValues,
            selectedNewPatientClaimsValues,
            selectedTopProcedures,
            selectedCareJourneyCostValues,
            selectedCareJourneyQualityValues,
            selectedTopConditions,
            selectedSpecialtyPredictionModelFilter,
            selectedPgrConfidenceTiers
          );

          if (projectionProvidersByType) {
            const excluded = projectionProvidersByType.find((pp) => pp.projectionType === ProjectionProviderType.ExcludeImpact);
            const targeted = projectionProvidersByType.find((pp) => pp.projectionType === ProjectionProviderType.Target);

            // A response omitting a projection type means zero providers of that type for the
            // new selection, not "unchanged" — falling back to the prior records here would show
            // stale excluded/targeted providers after a ZIP or specialty change.
            excludedRecords = excluded?.providers ?? [];
            targetedRecords = targeted?.providers ?? [];
          }
        }

        if (this.props.runConfiguration.isProjected || this.props.runConfiguration.isProjectedTermed) {
          this.setState({
            disabledGainsFilter: true,
          });
        }
        else {
          this.setState({
            disabledGainsFilter: false,
          });
        }

        // Only refresh Servicing when the projection view is now empty and no search is applied, since only then does it fall back to Servicing.
        const displayedProjectionRecords = this.state.displayedProviderType === "excluded" ? excludedRecords : targetedRecords;
        const shouldFetchServicing = this.state.displayedProviderType === "servicing"
          || (shouldRefreshServicingList && !searchString && displayedProjectionRecords.length === 0);

        let servicingProviderResult: ServicingProviderResult = shouldFetchServicing ?
          await this.projectService.servicingProviders(
            this.props.project.projectId,
            this.props.regionSetId,
            this.props.runConfiguration.projectRunConfigurationId,
            this.props.specialtyGroupCode,
            this.props.providerNetworkType,
            this.isZipProject ? NetworkAssessmentAnalysisType.ServiceArea : this.props.runConfiguration.networkAssessmentAnalysisType,
            gainsPercent,
            offset,
            this.batchCount,
            selectedColumn as string,
            sortDirection,
            columnSubKey,
            this.state.displayedProviderType === "servicing" ? searchString : "",
            selectedEffectivenessValues,
            selectedEfficiencyValues,
            selectedVolumeLabels,
            selectedLineOfBusiness,
            selectedLocationConfidenceValues,
            selectedMotiveAppropriatenessValues,
            selectedNewPatientClaimsValues,
            selectedTopProcedures,
            selectedCareJourneyCostValues,
            selectedCareJourneyQualityValues,
            selectedTopConditions,
            selectedSpecialtyPredictionModelFilter,
            selectedPgrConfidenceTiers
          ) :
          {
            batchCount: this.batchCount,
            hasExcludes: excludedRecords.length > 0,
            hasTargets: targetedRecords.length > 0,
            offset,
            providers: this.state.sortedRecords,
            topConditions: this.state.topConditions.map(x => x.key),
            topProcedures: this.state.topProcedures.map(x => x.key),
            totalCount: this.state.totalCount,
          };

        if (this.state.displayedProviderType !== "servicing") {
          // The servicing response reports projections unfiltered, so toggle visibility follows the filtered ones.
          servicingProviderResult = {
            ...servicingProviderResult,
            hasExcludes: excludedRecords.length > 0,
            hasTargets: targetedRecords.length > 0,
          };
        }

        if (requestSequence !== this.latestRequestSequence) {
          return;
        }

        if (!isEqual(servicingProviderResult?.topConditions.map(x => x.code), this.state.topConditions.map(x => x.key.code))) {
          const mappedTopConditions: DropdownItem<TopCondition>[] = servicingProviderResult?.topConditions.map(x => {
            return { key: x, name: `${x.code} - ${x.description}` }
          });
          this.setState({ topConditions: mappedTopConditions });
        }

        if (!isEqual(servicingProviderResult?.topProcedures.map(x => x.code), this.state.topProcedures.map(x => x.key.code))) {
          const mappedTopProcedures: DropdownItem<TopProcedure>[] = servicingProviderResult?.topProcedures.map(x => {
            return { key: x, name: `${x.code} - ${x.description}` }
          });
          this.setState({ topProcedures: mappedTopProcedures });
        }

        let displayedProviderType: DisplayedProviderType = this.state.displayedProviderType || "servicing";
        let loadStatus: LoadStatus = "error";
        let records: ServicingProvider[] = [];
        if (searchString) {
          displayedProviderType = this.state.displayedProviderType;
          switch (this.state.displayedProviderType) {
            case "excluded":
              loadStatus = excludedRecords.length > 0 ? "loaded" : "empty";
              break;
            case "servicing":
              loadStatus = servicingProviderResult.providers.length > 0 ? "loaded" : "empty";
              if (forceLoad) {
                records = [...servicingProviderResult.providers];

                if (!isEqual(servicingProviderResult?.topConditions.map(x => x.code), this.state.topConditions.map(x => x.key.code))) {
                  const mappedTopConditions: DropdownItem<TopCondition>[] = servicingProviderResult?.topConditions.map(x => {
                    return { key: x, name: `${x.code} - ${x.description}` }
                  });

                  this.setState({ topConditions: mappedTopConditions });
                }

                if (!isEqual(servicingProviderResult?.topProcedures.map(x => x.code), this.state.topProcedures.map(x => x.key.code))) {
                  const mappedTopProcedures: DropdownItem<TopProcedure>[] = servicingProviderResult?.topProcedures.map(x => {
                    return { key: x, name: `${x.code} - ${x.description}` }
                  });

                  this.setState({ topProcedures: mappedTopProcedures });
                }
              } else {
                records = [...this.state.sortedRecords, ...servicingProviderResult.providers];
              }
              break;
            case "targeted":
              loadStatus = targetedRecords.length > 0 ? "loaded" : "empty";
              break;
          }
        } else if (servicingProviderResult && servicingProviderResult.providers) {
          if (servicingProviderResult.providers.length > 0) {
            loadStatus = "loaded";
            if (forceLoad) {
              records = [...servicingProviderResult.providers];
            } else {
              records = [...this.state.sortedRecords, ...servicingProviderResult.providers];
            }
            if (displayedProviderType !== "servicing" && !this.state.hasTargets && !this.state.hasExcludes) {
              displayedProviderType = "servicing";
            }
            if (this.props.providerNetworkType === ProviderNetworkTypeEnum.Prospect || this.props.providerNetworkType === ProviderNetworkTypeEnum.Market) {
              servicingProviderResult.providers.forEach((provider) => {
                provider.cargoData.forEach((cd) => {
                  this.cargoColumns.add(cd.name);
                });
              });
            }
          } else if (gainsPercent !== GainsPercentEnum.All) {
            loadStatus = "loaded";
          } else {
            loadStatus = "empty";
          }
        }

        if (this.state.hasExcludes) {
          excludedRecords.forEach((provider) => {
            provider.cargoData.forEach((cd) => {
              this.cargoColumns.add(cd.name);
            });
          });
        }
        if (this.state.hasTargets) {
          targetedRecords.forEach((provider) => {
            provider.cargoData.forEach((cd) => {
              this.cargoColumns.add(cd.name);
            });
          });
        }

        const newFlyoutRefs = [];
        const providersForButtons = this.state.displayedProviderType === "excluded" ? excludedRecords : this.state.displayedProviderType === "targeted" ? targetedRecords : [];
        for (const provider of providersForButtons) {
          newFlyoutRefs[provider.providerId] = React.createRef<HTMLButtonElement>();
        }
        this.flyoutButtonRefs.current = newFlyoutRefs;

        this.setState((prevState) => ({
          ...prevState,
          ...servicingProviderResult,
          columnConfig: this.getColumnConfig(),
          displayedProviderType,
          excludedRecords,
          gainsPercent,
          hasServicing: this.state.hasServicing || servicingProviderResult.totalCount > 0,
          lastSearchTerm: searchString,
          lastSelectedCareJourneyCostValues: selectedCareJourneyCostValues,
          lastSelectedCareJourneyQualityValues: selectedCareJourneyQualityValues,
          lastSelectedConditions: selectedTopConditions,
          lastSelectedEffectivenessValues: selectedEffectivenessValues,
          lastSelectedEfficiencyValues: selectedEfficiencyValues,
          lastSelectedLineOfBusiness: selectedLineOfBusiness,
          lastSelectedLocationConfidenceValues: selectedLocationConfidenceValues,
          lastSelectedMotiveAppropriatenessValues: selectedMotiveAppropriatenessValues,
          lastSelectedNewPatientClaimsValues: selectedNewPatientClaimsValues,
          lastSelectedPgrConfidenceTiers: selectedPgrConfidenceTiers,
          lastSelectedProcedures: selectedTopProcedures,
          lastSelectedSpecialtyPredictionModelFilter: selectedSpecialtyPredictionModelFilter,
          lastSelectedVolumeLabels: selectedVolumeLabels,
          lastSortedCargo: columnSubKey,
          lastSortedColumn: selectedColumn,
          lastSortedDirection: sortDirection,
          loadStatus,
          offset: offset + servicingProviderResult.batchCount,
          sortedRecords: records,
          tableData: this.buildTable(records, "servicing"),
          tableDataExcluded: this.buildTable(excludedRecords, "excluded"),
          tableDataTargeted: this.buildTable(targetedRecords, "targeted"),
          targetedRecords,
          topConditions: prevState.topConditions,
          topProcedures: prevState.topProcedures,
          totalCount: servicingProviderResult.totalCount,
        }));
      }
    } catch (ex) {
      if (requestSequence !== undefined && requestSequence !== this.latestRequestSequence) {
        return;
      }
      this.setState((prevState) => ({
        ...prevState,
        excludedRecords: new Array<ServicingProvider>(),
        loadStatus: "error",
        sortedRecords: new Array<ServicingProvider>(),
        tableData: new Array<ImpactProviderTableDataWithCargo>(),
        tableDataExcluded: new Array<ImpactProviderTableDataWithCargo>(),
        tableDataTargeted: new Array<ImpactProviderTableDataWithCargo>(),
        targetedRecords: new Array<ServicingProvider>()
      }));
    }
  }

  private getPreviewTargetingContent = (priorProjectionProviderType: ProjectionProviderType) => {
    const showAvgDist = this.props.runConfiguration.showDistance && this.props.project.showAverageDistance;
    const showAvgTime = this.props.runConfiguration.showTime && this.props.project.showAverageTime;
    const showWithTimeAndDistanceColumns = this.props.runConfiguration.showWithTimeAndDistanceColumns;
    const showNetNew = this.props.user.roles.includes(Roles.VOLUME_TARGET);
    return (
      <SimulateProviderTargeting
        processingContent={(() => this.getCreateProjectionProvider(ProjectionProviderType.Target, priorProjectionProviderType))()}
        projectionProviderType={ProjectionProviderType.Target}
        providerNetworkType={this.props.providerNetworkType}
        regionSetId={this.props.regionSetId}
        selectedProviders={this.state.selectedProviders}
        specialtyGroupCode={this.props.specialtyGroupCode}
        visibility={{ showAvgDist, showAvgTime, showNetNew, showWithTimeAndDistanceColumns, }}
        user={this.props.user}
        formState={this.props.formState}
        formDispatch={this.props.formDispatch}
        isZipProject={this.isZipProject}
      />
    );
  }

  private getCreateProjectionProvider = (projectionProviderType: ProjectionProviderType, priorProjectionProviderType: ProjectionProviderType) => {
    let priorProjectionProviderProjectIds: number[];

    if (priorProjectionProviderType) {
      const allRecords = this.state.sortedRecords.concat(this.state.targetedRecords, this.state.excludedRecords);
      const selectedRecords = allRecords.filter((r) => !!this.state.selectedProviders.find((provider) => provider.providerId === r.providerId));
      priorProjectionProviderProjectIds =
        selectedRecords.map((r) => {
          const projectionProvider = r as ProjectionProvider;
          return projectionProvider.projectionProviderProjectId;
        });
    }

    return (
      <ProjectionProviderDetails
        {...this.props}
        accessPoints={convertServicingProvidersToAccessPoints(this.state.selectedProviders, this.props.providerNetworkType, this.props.specialtyGroupCode)}
        formState={this.props.formState}
        formDispatch={this.props.formDispatch}
        hasProvidersWithoutNPI={this.state.selectedProviders.some((provider) => provider.npi === undefined)}
        networkAssessmentAnalysisType={this.props.runConfiguration.networkAssessmentAnalysisType}
        priorProjectionProviderProjectIds={priorProjectionProviderProjectIds}
        priorProjectionProviderType={priorProjectionProviderType}
        projectId={this.props.project.projectId}
        projectionPanelMode={ProjectionPanelMode.Create}
        projectionProviderType={projectionProviderType}
        providerNetworkType={this.props.providerNetworkType}
        onProjectionProvidersCreated={this.handleProjectionProviderCreatedOrUpdated}
        specialtyGroupCode={this.props.specialtyGroupCode}
        isZipProject={this.isZipProject}
        showAffiliationsInsights={this.props.project.showAffiliationsInsights}
        isRunning={this.props.project?.isRunning}
        isRegulatorProject={!!this.props.project?.networkTaxonomy?.regulatorAssignmentClientId}
        regulatorAssignmentClient={this.props.project?.networkTaxonomy?.regulatorAssignmentClient ?? ""}
      />
    );
  }

  private handleOpenEditProviderFlyout = (projectionProvider: ProjectionProvider, projectionProviderType: ProjectionProviderType, canEdit: boolean) => {
    this.setState({
      selectedCanEdit: canEdit,
      selectedFlyoutButtonRef: projectionProvider.providerId,
      selectedProjectionProvider: projectionProvider,
      selectedProjectionProviderType: projectionProviderType
    });
    this.props.setIsFlyoutVisible(true);
  }

  private getDeleteProjectionProvider = (projectionProviderType: ProjectionProviderType) => {
    const allRecords = this.state.sortedRecords.concat(this.state.targetedRecords, this.state.excludedRecords);
    const selectedRecords = allRecords.filter((r) => !!this.state.selectedProviders.find((provider) => provider.providerId === r.providerId));
    const projectionProviderProjectIds =
      selectedRecords.map((r) => {
        const projectionProvider = r as ProjectionProvider;
        return projectionProvider.projectionProviderProjectId;
      });

    return (
      <DeleteProjectionProvider
        netNewServicingProviderProjectIds={[]}
        projectId={this.props.project.projectId}
        projectionProviderProjectIds={projectionProviderProjectIds}
        projectionProviderType={projectionProviderType}
        providerNetworkType={this.props.providerNetworkType}
        onProjectionProvidersDeleted={this.handleProjectionProvidersDeleted}
        wrapperComponentType="flyoutLink"
      />
    );
  }

  private getProviderProfile = (provider: ServicingProvider): React.ReactNode => {
    return (
      <ServicingProviderProfile
        {...this.props}
        provider={provider}
        providerNetworkType={this.props.providerNetworkType}
        isProjected={["excluded", "targeted"].includes(this.state.displayedProviderType)}
      />
    );
  }

  private handleDistanceClick = async () => {
    if (this.state.selectedProviders.length === 2) {
      const fromProvider = this.state.selectedProviders[0];
      const toProvider = this.state.selectedProviders[1];

      const distance = await this.geoService.getDrivingDistance(fromProvider.longitude, fromProvider.latitude, toProvider.longitude, toProvider.latitude);

      toast(distance, this.toastId);
    }
  }

  private handleMainPanelDidClose = () => {
    this.setState({ isOpen: false });
  }

  private handleMainPanelDidOpen = () => {
    this.setState({ gainsPercent: GainsPercentEnum.All, isOpen: true });
  }

  private handleProjectionProviderCreatedOrUpdated = async (runProjection: boolean, hasUnprocessedProviders = true) => {
    if (runProjection) {
      this.handleProcessProviderChanges(false);
    } else {
      this.handleUpdateUnprocessedProviderChanged(hasUnprocessedProviders || this.props.project.hasUnprocessedProjectionProviders);
      this.resetState();
    }
  }

  private handleProjectionProvidersDeleted = async (runProjection: boolean) => {
    if (runProjection) {
      this.handleProcessProviderChanges(false);
    } else {
      if (!this.isZipProject)
        this.props.setProjectHasUnprocessedProjectionProviders(true);
      else
        this.props.zipContextEvents.onUpdateUnprocessedProjectionProviders(true);
      if (this.tableType === TableType.MarketProviders) {
        this.props.setShouldRefreshMarketProviders(true);
      } else {
        this.props.setShouldRefreshProspectProviders(true);
      }
      this.resetState();
    }
  }

  private handleProcessProviderChanges = async (runProjection: boolean) => {
    if (!this.isZipProject)
      this.props.processProviderChanges(runProjection);
    else
      this.props.zipContextEvents.onProcessProviderChanges(runProjection);
  }

  private handleUpdateUnprocessedProviderChanged = async (hasUnprocessedProviders: boolean) => {
    if (!this.isZipProject)
      this.props.setProjectHasUnprocessedProjectionProviders(hasUnprocessedProviders);
    else
      this.props.zipContextEvents.onUpdateUnprocessedProjectionProviders(hasUnprocessedProviders);

    if (this.tableType === TableType.MarketProviders) {
      this.props.setShouldRefreshMarketProviders(true);
    } else {
      this.props.setShouldRefreshProspectProviders(true);
    }
  }

  private resetState = () => {
    const freshColumnConfig = this.getColumnConfig();
    this.setState({
      columnConfig: freshColumnConfig,
      excludedRecords: [],
      lastSortedCargo: undefined,
      lastSortedColumn: undefined,
      lastSortedDirection: undefined,
      loadStatus: "shouldLoad",
      offset: 0,
      selectedProviders: [],
      sortedRecords: [],
      tableData: [],
      tableDataExcluded: [],
      tableDataTargeted: [],
      targetedRecords: [],
      totalCount: 0,
    });
  }

  private handleToggleDisplayedProviderType = (displayedProviderType: DisplayedProviderType) => {
    let newState: Partial<ImpactProvidersPanelState>;
    // if an inactive toggle was clicked, just go there
    if (this.state.displayedProviderType !== displayedProviderType) {
      newState = {
        displayedProviderType,
        selectedProviders: [],
      };
    } else {
      // if the active toggle was clicked and we don't have servicing providers,
      // try to switch to the other projection type first, then fall back on servicing
      if (!this.state.hasServicing) {
        if (displayedProviderType === "targeted" && this.state.hasExcludes) {
          newState = {
            displayedProviderType: "excluded",
            selectedProviders: [],
          };
        } else if (displayedProviderType === "excluded" && this.state.hasTargets) {
          newState = {
            displayedProviderType: "targeted",
            selectedProviders: [],
          };
        } else {
          newState = {
            displayedProviderType: "servicing",
            selectedProviders: [],
          };
        }
        // or if we have servicing just do that
      } else {
        newState = {
          displayedProviderType: "servicing",
          selectedProviders: [],
        };
      }
    }

    this.setState({
      ...newState as ImpactProvidersPanelState,
      // Only a toggle click clears the filter row; an automatic fallback keeps the applied filters.
      displayedProviderTypeChanged: true,
      lastSearchTerm: "",
      lastSelectedCareJourneyCostValues: undefined,
      lastSelectedCareJourneyQualityValues: undefined,
      lastSelectedConditions: undefined,
      lastSelectedEffectivenessValues: undefined,
      lastSelectedEfficiencyValues: undefined,
      lastSelectedLineOfBusiness: undefined,
      lastSelectedLocationConfidenceValues: undefined,
      lastSelectedMotiveAppropriatenessValues: undefined,
      lastSelectedNewPatientClaimsValues: undefined,
      lastSelectedProcedures: undefined,
      lastSelectedVolumeLabels: undefined,
      offset: 0,
      topConditions: this.state.topConditions,
      topProcedures: this.state.topProcedures
    }, () => {
      this.buildServicingProviders(true);
    })
  }

  private providerRecordClassDecorator = (row: ImpactProviderTableDataWithCargo): string => {
    const currentRecordSet = this.state.displayedProviderType === "excluded" ? this.state.excludedRecords :
      this.state.displayedProviderType === "servicing" ? this.state.sortedRecords :
        this.state.displayedProviderType === "targeted" ? this.state.targetedRecords : [];
    const record = currentRecordSet.find((r) => r.providerId.toString() === row[wafflesKeyColumnName]);
    const passPercentage = this.isZipProject ? this.props.zipDetails[0]?.minPassPercent : this.props.accessSummary.passPercentage;
    if (!record || !passPercentage) {
      return "";
    }

    // TODO: This logic needs to be pushed up to the servicing provider service - add a new property there to indicate the results and translate that property value to danger/success/warning here
    let statusDecorator = getProviderGapStatusDecoratorWithFlags(
      this.props.enabledFeatureFlags,
      record.gainsPercent,
      record.impactsPercent,
      record.servesPercent,
      passPercentage
    ).toString();

    if (this.state.displayedProviderType === "targeted" || this.state.displayedProviderType === "excluded") {
      const projectionProvider = record as ProjectionProvider;

      if (projectionProvider && !projectionProvider.processed) {
        statusDecorator = `muted ${statusDecorator}`;
      }
    }

    return statusDecorator;
  }

  private handleProviderSearch = (
    searchValue: string,
    selectedEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    selectedEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    selectedVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    selectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    selectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    selectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
    selectedCareJourneyCostValues?: CareJourneyScoresValueEnum[],
    selectedCareJourneyQualityValues?: CareJourneyScoresValueEnum[],
    gainsPercent?: GainsPercentEnum,
    selectedNewPatientClaimsValues?: NewPatientClaimsEnum[],
    selectedTopProcedures?: string[],
    selectedTopConditions?: string[],
    selectedSpecialtyPredictionModelFilter?: SpecialtyPredictionModelEnum,
    selectedPgrConfidenceTiers?: number[]
  ): void => {
    const { lastSortedColumn, lastSortedDirection, lastSortedCargo } = this.state;

    this.buildServicingProviders(
      true,
      lastSortedColumn,
      lastSortedDirection,
      lastSortedCargo,
      true,
      searchValue,
      gainsPercent,
      selectedCareJourneyCostValues,
      selectedCareJourneyQualityValues,
      selectedEffectivenessValues,
      selectedEfficiencyValues,
      selectedVolumeLabels,
      selectedLineOfBusiness,
      selectedLocationConfidenceValues,
      selectedMotiveAppropriatenessValues,
      selectedNewPatientClaimsValues,
      selectedTopProcedures,
      selectedTopConditions,
      selectedSpecialtyPredictionModelFilter,
      selectedPgrConfidenceTiers,
      // Servicing is not refetched while Targeted/Excluded is shown, so refresh it if the view falls back to Servicing.
      this.state.displayedProviderType !== "servicing"
    );
  }

  private handleConfidenceTierSearch = (searchValue: string, selectedPgrConfidenceTiers: number[]): void => {
    this.handleProviderSearch(
      searchValue,
      undefined, undefined, undefined, undefined, undefined, undefined, undefined,
      undefined, undefined, undefined, undefined, undefined, undefined,
      selectedPgrConfidenceTiers
    );
  }

  private updateChangeTracker = () => {
    if (this.state.displayedProviderTypeChanged) {
      this.setState({
        displayedProviderTypeChanged: false,
      });
    }
  }

  private get providerSearchDescriptiveText(): string {
    return `This will search each provider's name, NPI,${this.props.user.roles.includes(Roles.TIN_VIEWER) ? " TIN," : ""} specialty, and address.`;
  }

  private get searchFiltersLoaded(): boolean {
    return this.state.loadStatus === "loaded" || this.state.loadStatus === "empty";
  }

  /**
   * The props both filter rows need. Anything added here reaches each row without being wired up twice.
   */
  private get sharedSearchFilterProps() {
    return {
      lastSearchValue: this.state.lastSearchTerm,
      lastSelectedConfidenceTierValues: this.state.lastSelectedPgrConfidenceTiers,
      tableType: this.tableType,
      updateChangeTracker: this.updateChangeTracker,
      validLoadStatus: this.searchFiltersLoaded,
    };
  }

  private renderSearchField = (): JSX.Element => {
    if (this.props.project.showPciData) {
      return (
        <ProviderClaimsFilters
          {...this.sharedSearchFilterProps}
          clearFilters={this.state.displayedProviderTypeChanged}
          handleProviderSearch={this.handleProviderSearch}
          lastSelectedConditionValues={this.state.lastSelectedConditions}
          lastSelectedEffectivenessValues={this.state.lastSelectedEffectivenessValues}
          lastSelectedEfficiencyValues={this.state.lastSelectedEfficiencyValues}
          lastSelectedGainsPercent={this.state.gainsPercent}
          lastSelectedLineOfBusiness={this.state.lastSelectedLineOfBusiness}
          lastSelectedVolumeLabels={this.state.lastSelectedVolumeLabels}
          lastSelectedLocationConfidenceValues={this.state.lastSelectedLocationConfidenceValues}
          lastSelectedMotiveAppropriatenessValues={this.state.lastSelectedMotiveAppropriatenessValues}
          lastSelectedNewPatientClaimsValues={this.state.lastSelectedNewPatientClaimsValues}
          lastSelectedCareJourneyCostValues={this.state.lastSelectedCareJourneyCostValues}
          lastSelectedCareJourneyQualityValues={this.state.lastSelectedCareJourneyQualityValues}
          lastSelectedProcedureValues={this.state.lastSelectedProcedures}
          showGainsPercent={true}
          showLocationConfidence={this.props.runConfiguration.showLocationConfidenceScores && this.props.project.showLocationConfidenceScores}
          showCareJourneyScores={this.props.runConfiguration.showCareJourneyScores}
          showSpecialtyPredictionModel={this.props.enabledFeatureFlags.has(Features.NETWORK_PERFORMANCE_SPECIALTY_PREDICTION_MODEL)}
          lastSelectedSpecialtyPredictionModelFilter={this.state.lastSelectedSpecialtyPredictionModelFilter}
          topConditions={this.state.topConditions}
          topProcedures={this.state.topProcedures}
          showConfidenceTier={this.showPgrConfidenceTier()}
        />
      );
    } else if (this.showPgrConfidenceTier()) {
      return (
        <ConfidenceTierSearchFilters
          {...this.sharedSearchFilterProps}
          clearSearch={this.state.displayedProviderTypeChanged}
          descriptiveText={this.providerSearchDescriptiveText}
          handleProviderSearch={this.handleConfidenceTierSearch}
        />
      );
    } else {
      return (
        <SearchBox
          key={this.state.displayedProviderType}
          descriptiveText={this.providerSearchDescriptiveText}
          fieldsetClass="d-flex"
          inputLabel="Search Providers"
          testId={`${this.props.providerNetworkType}-provider-search`}
          onSubmit={(value: string) => { this.handleProviderSearch(value) }}
        />
      );
    }
  }

  private get allowTargetExclude(): boolean {
    return this.props.runConfiguration.isLatestPublished
      || this.props.runConfiguration.datePublished == null
      || this.props.runConfiguration.isTermed
      || this.props.runConfiguration.isProjected
      || this.props.runConfiguration.isProjectedTermed;
  }

  private renderProviderTable = () => {
    const networkPrefix = this.props.providerNetworkType === ProviderNetworkTypeEnum.Market
      ? "market "
      : "";
    const passPercentage = this.isZipProject ? this.props.zipDetails[0]?.minPassPercent : this.props.accessSummary?.passPercentage;
    const accessPercentage = this.isZipProject ? this.props.zipDetails[0]?.withAccessPercent : this.props.accessSummary?.memberAccessPercentage;
    const leadText = (passPercentage != null)
      ? (accessPercentage < 100)
        ? (<>The following {networkPrefix}prospect providers are capable of servicing beneficiaries.  <strong className="text-success">Green </strong>impacts access to at least {passPercentage}%, <strong className="text-warning">yellow </strong>impacts access to at least 25%, otherwise <strong className="text-danger">red</strong>.</>)
        : (<>The following {networkPrefix}prospect providers are potential alternatives to the current servicing providers.  <strong className="text-success">Green </strong>serves at least {passPercentage}%, <strong className="text-warning">yellow </strong>serves at least 25%, otherwise <strong className="text-danger">red</strong>.</>)
      : null;

    const zipTargetingEnabled = !this.isZipProject || this.props.enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TARGETING);
    const hasExcludedRecords = this.allowTargetExclude && this.state.hasExcludes && zipTargetingEnabled;
    const hasTargetedRecords = this.allowTargetExclude && this.state.hasTargets && zipTargetingEnabled;
    const hasProjectionProviders = hasExcludedRecords || hasTargetedRecords;

    return (
      <>
        <p className={hasProjectionProviders ? "mb0" : ""}>{leadText}</p>
        {!this.props.project.showPciData && (
          <div className="d-flex justify-content-end">
            <div>
              <label className="label-s me-3 d-inline-block" htmlFor={`claims-activity__filters--gains-percent-${this.props.providerNetworkType}`}>
                Display
              </label>
              <TooltipWrapper
                text={this.state.disabledGainsFilter ? "The Gains Percent filter is unavailable for projected and projected-termed timeslices." : ""}
              >
                <GainsPercentFilter
                  containerClassName="d-inline-block"
                  gainsPercent={this.state.gainsPercent}
                  hideLabel={true}
                  setGainsPercent={this.setPercentGains}
                  testId={`claims-activity__filters--gains-percent-${this.props.providerNetworkType}`}
                  disabledGainsFilter={this.state.disabledGainsFilter}
                />
              </TooltipWrapper>
            </div>
          </div>
        )}

        <div className="clearfix">
          <div className="pull-right">
            {hasExcludedRecords && (
              <Toggle
                label="Excluded"
                active={this.state.displayedProviderType === "excluded"}
                onChange={() => this.handleToggleDisplayedProviderType("excluded")}
                disabled={!(this.state.loadStatus === "loaded" || this.state.loadStatus === "empty")}
                containerClassName="m-3"
                testId={`${this.props.providerNetworkType}-provider-panel__toggle-excluded-providers`}
              />
            )}
            {hasTargetedRecords && (
              <Toggle
                label="Targeted"
                active={this.state.displayedProviderType === "targeted"}
                onChange={() => this.handleToggleDisplayedProviderType("targeted")}
                disabled={!(this.state.loadStatus === "loaded" || this.state.loadStatus === "empty")}
                containerClassName="m-3"
                testId={`${this.props.providerNetworkType}-provider-panel__toggle-targeted-providers`}
              />
            )}
          </div>
        </div>
        {this.renderTable()}
      </>
    );
  }

  private setPercentGains = (gainsPercent: GainsPercentEnum) => {
    this.setState({
      gainsPercent,
    });
  }

  private renderTable = () => {
    const records = this.state.displayedProviderType === "excluded"
      ? this.state.tableDataExcluded.slice(0, this.state.offset)
      : this.state.displayedProviderType === "servicing"
        ? this.state.tableData
        : this.state.tableDataTargeted.slice(0, this.state.offset);

    return (
      <Waffles<ImpactProviderTableDataWithCargo>
        bulkActionBar={this.getBulkActionHeader()}
        columns={this.state.columnConfig}
        customSort={this.customSort}
        loading={this.state.loadStatus !== "loaded" && this.state.loadStatus !== "empty"}
        loadMore={this.handleLoadMore}
        data={records}
        fullWidth={true}
        handleCheckboxChange={this.onCheckboxChange}
        key={this.state.displayedProviderType}
        passThroughSorting={true}
        rowDecorator={this.providerRecordClassDecorator}
        selectedKeys={this.state.selectedProviders.map((provider) => provider.providerId.toString())}
        showCheckboxes={true}
        testId={`${this.tableType}-table`}
        totalNumberOfRecords={this.state.totalCount}
      />
    );
  }

  private getBulkActionHeader = () => {
    let priorProjectionProviderType: ProjectionProviderType = null;
    let showRemoveExcluded = false;
    let showRemoveTargeted = false;

    switch (this.state.displayedProviderType) {
      case "excluded":
        showRemoveExcluded = this.allowTargetExclude;
        priorProjectionProviderType = ProjectionProviderType.ExcludeImpact;
        break;
      case "targeted":
        showRemoveTargeted = this.allowTargetExclude;
        priorProjectionProviderType = ProjectionProviderType.Target;
        break;
    }

    const showTarget = this.allowTargetExclude && !showRemoveTargeted && !this.props.project.isEditRestricted && (!this.isZipProject || this.props.enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TARGETING));
    const showExclude = this.allowTargetExclude && !showRemoveExcluded && !this.props.project.isEditRestricted && (!this.isZipProject || this.props.enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TARGETING));

    const granularity = RegionGranularity.County;
    const networkTypes = [this.props.providerNetworkType];
    const regionSetIds = [this.props.regionSetId];
    const specialtyGroupCodes = [this.props.specialtyGroupCode];
    const providerIds = this.state.selectedProviders.map((provider) => provider.providerId);
    const providerAvailabilityType = this.state.displayedProviderType === "excluded" ? ExportProviderAvailabilityType.Excluded : this.state.displayedProviderType === "targeted" ? ExportProviderAvailabilityType.Targeted : ExportProviderAvailabilityType.Available;

    const hasSelectedRecords = this.state.selectedProviders.length > 0;

    const excludeDisabledToolTip = this.excludedDisabledToolTip(this.state.selectedProviders);
    const targetDisabledToolTip = this.targetedDisabledToolTip(this.state.selectedProviders);

    const selectedMemberships: number[] = [this.props.runConfiguration.memberSetId];
    if (this.props.isDualMembershipMode) {
      // county context vs zip context
      selectedMemberships.push((this.props.compareToRunConfiguration || this.props.dualMembershipRunConfiguration).memberSetId);
    }

    const { primaryRunConfiguration, secondaryRunConfiguration } = getPrimaryAndSecondaryRunConfigurations(this.props.project.projectRunConfigurations, this.props.runConfiguration.projectRunConfigurationId);
    return (
      <>
        <span className="bulk-action-group">
          <Link<Partial<ExportDataRouteState> & ReportCustomizationRouteState>
            to={{
              pathname: this.props.exportEndpoint,
              state: {
                activeModule: this.props.module,
                activeNetworkType: this.props.providerNetworkType,
                adequacyFilters: this.props.adequacyFilters,
                availableExportTypes: this.isZipProject ? [ExportType.ZipAnalysisAdequacyServicingProvider] : [ExportType.ServicingProviders],
                disableRegionFilters: true,
                disableSpecialtyGroupFilters: true,
                filters: this.props.filters,
                granularity,
                initialNetworkAssessmentAnalysisType: this.props.networkAssessmentAnalysisType,
                networkTypes,
                projectResourceId: this.props.project.resource.resourceId,
                projectResourceName: this.props.project.resource.name,
                projectURL: this.props.projectURL,
                providerAvailabilityType,
                providerIds,
                regionSetIds,
                specialtyGroupCodes,

                // new reportcustomization
                /* oxlint-disable sort-keys */
                projectName: this.props.project.resource.name,
                isZipProject: this.isZipProject,
                showClosestProviderToMemberReport: this.props.project.showClosestProviderReport,
                defaultReportType: this.isZipProject ? ReportType.ZipAnalysisAdequacyServicingProvider : ReportType.AdequacyServicingProvider,
                defaultProviderSource: this.props.providerNetworkType,
                defaultProviderFilter: providerAvailabilityType,
                networkAssessmentAnalysisType: this.props.networkAssessmentAnalysisType,
                primaryMembership: { alias: primaryRunConfiguration.memberSetAlias || primaryRunConfiguration.memberSetName, id: primaryRunConfiguration.memberSetId },
                secondaryMembership: { alias: secondaryRunConfiguration?.memberSetAlias || secondaryRunConfiguration?.memberSetName, id: secondaryRunConfiguration?.memberSetId },
                selectedMemberships,
                selectedTimeslice: this.props.runConfiguration.projectRunConfigurationId, // runConfigurationId
                selectedRegionSetIds: !!this.props.adequacyResult?.details[0] ? [this.props.adequacyResult.details[0].regionSetId] : undefined,
                isCompareMode: !!this.props.compareToRunConfiguration && !this.props.isDualMembershipMode,
                selectedComparedTimeslice: this.props.compareToRunConfiguration?.projectRunConfigurationId, // compareToRunConfigurationId
                isViewingOpportunity: this.props.adequacyFilters.opportunity,
                runFrequency: this.props.runFrequency || TimeFrequency.All,
                showAddedProviders: !!this.props.compareToRunConfiguration,
                showMatchedProviders: !!this.props.compareToRunConfiguration,
                showRemovedProviders: !!this.props.compareToRunConfiguration,
                showPatternOfCare: this.props.project.showPatternOfCareProviders,
                hasProspectProviders: !!this.props.runConfiguration.prospectProviderSetId,
                hasMarketProviders: !!this.props.runConfiguration.marketProviderSetId,
                showAdvancedComparison: this.props.project.showAdvancedComparison,
                showExpandableSpecialties: !!this.props.adequacyResult?.details?.some((d) => d.canExpandSpecialties),
                selectedSpecialties: !!this.props.filterSelections?.specialtyGroupCode ? [this.props.filterSelections.specialtyGroupCode.key] : [],
                zipProjectSearchParameters: this.isZipProject ? getSearchParametersFromFilterSelections(this.props.filterSelections) : undefined,
                showRegionSummaryReport: this.props.project.hasRegions,
                /* oxlint-enable */
              },
            }}
            data-testid={`${this.tableType}__create-report-button`}
          >
            <QesIcon iconName={QesIcons.DownloadCloudV1} description="Download" className="align-text-top me-1" /> Create Report
          </Link>
        </span>

        <span className={`bulk-action-group ms-3`}>
          <TooltipWrapper text="Measure distance between two selected providers">
            <QESButton qesType="secondary" className="px-0" disabled={this.state.selectedProviders.length !== 2} onClick={this.handleDistanceClick} qesTestId={`impact-providers-panel__distance--${this.props.providerNetworkType}`}>
              <QesIcon iconName={QesIcons.Route} description="Distance" className="align-text-top me-1" /> Distance
            </QESButton>
          </TooltipWrapper>
        </span>

        <RoleRestricted allowedRoles={[Roles.PROVIDER_EXCLUDE_USER]}>
          {(showExclude || null) && (
            <span className={`bulk-action-group ms-3${(hasSelectedRecords && excludeDisabledToolTip === "") ? "" : " disabled"}`}>
              <FlyoutLink
                classNames="p-0"
                content={{
                  body: this.getCreateProjectionProvider(ProjectionProviderType.ExcludeImpact, priorProjectionProviderType),
                  title: `Exclude ${capitalizeFirstLetter(this.providerPlurality)}`
                }}
                disabled={!hasSelectedRecords}
                onClose={() => {
                  this.props.formDispatch({ type: ProjectionProviderDetailsActionType.Reset })
                }}
                testId="impact-providers-panel__exclude-flyout"
              >
                <TooltipWrapper text={excludeDisabledToolTip === '' ? `Exclude selected ${this.providerPlurality}` : excludeDisabledToolTip}>
                  <QesIcon iconName={QesIcons.SlashOctagon} description="Exclude" className="align-text-top me-1" /> Exclude
                </TooltipWrapper>
              </FlyoutLink>
            </span>
          )}
        </RoleRestricted>

        {
          (showTarget || null) && (
            <RoleRestricted allowedRoles={[Roles.PROVIDER_TARGET_USER]}>
              <span className={`bulk-action-group ms-3${(hasSelectedRecords && targetDisabledToolTip === "") ? "" : " disabled"}`}>
                <FlyoutLink
                  classNames="p-0"
                  content={{
                    body: this.getPreviewTargetingContent(priorProjectionProviderType),
                    title: `Target ${capitalizeFirstLetter(this.providerPlurality)}`
                  }}
                  disabled={!hasSelectedRecords || targetDisabledToolTip !== ""}
                  onClose={() => this.props.formDispatch({ type: ProjectionProviderDetailsActionType.Reset })}
                  testId="impact-providers-panel__target-flyout--with-preview"
                >
                  <TooltipWrapper text={targetDisabledToolTip === '' ? `Preview and target selected ${this.providerPlurality}` : targetDisabledToolTip}>
                    <QesIcon iconName={QesIcons.TargetV4} description="Target" className="align-text-top me-1" /> Target
                  </TooltipWrapper>
                </FlyoutLink>
              </span>
            </RoleRestricted>
          )
        }

        <RoleRestricted allowedRoles={[Roles.PROVIDER_EXCLUDE_USER]}>
          {(showRemoveExcluded && !this.props.project.isEditRestricted) && (
            <span className={`bulk-action-group ms-3${hasSelectedRecords ? "" : " disabled"}`}>
              <FlyoutLink
                classNames="p-0"
                content={{
                  body: this.getDeleteProjectionProvider(ProjectionProviderType.ExcludeImpact),
                  title: `Delete Excluded ${capitalizeFirstLetter(this.providerPlurality)}`,
                }}
                disabled={!hasSelectedRecords}
                testId="impact-providers-panel__delete-flyout--excluded"
                size="20"
              >
                <TooltipWrapper text={`Stop excluding selected ${this.providerPlurality}`}>
                  <QesIcon iconName={QesIcons.TrashV1} description="Remove" className="align-text-top me-1" /> Remove
                </TooltipWrapper>
              </FlyoutLink>
            </span>
          )}
        </RoleRestricted>

        <RoleRestricted allowedRoles={[Roles.PROVIDER_TARGET_USER]}>
          {(showRemoveTargeted && !this.props.project.isEditRestricted) && (
            <span className={`bulk-action-group ms-3${hasSelectedRecords ? "" : " disabled"}`}>
              <FlyoutLink
                classNames="p-0"
                content={{
                  body: this.getDeleteProjectionProvider(ProjectionProviderType.Target),
                  title: `Delete Targeted ${capitalizeFirstLetter(this.providerPlurality)}`,
                }}
                disabled={!hasSelectedRecords}
                testId="impact-providers-panel__delete-flyout--target"
                size="20"
              >
                <TooltipWrapper text={`Stop targeting selected ${this.providerPlurality}`}>
                  <QesIcon iconName={QesIcons.TrashV1} description="Remove" className="align-text-top me-1" /> Remove
                </TooltipWrapper>
              </FlyoutLink>
            </span>
          )}
        </RoleRestricted>

        {this.isZipProject && (
          <FlyoutLink
            content={{
              body: (
                <DisplayOptions
                  defaultOpen={true}
                  projectId={this.props.project.projectId}
                  // this will need to be updated when projection is added to zip projects
                  viewablePanels={[this.tableType]}
                  onChange={(isDirty: boolean) => { this.setState({ displayOptionsDirty: isDirty }) }}
                  relevantTableType={this.tableType}
                />
              ),
              title: "Display Options",
            }}
            modalContent={{
              body: "You will lose unsaved changes. Continue with closing the sidebar?",
              cancelButtonText: "Cancel",
              confirmButtonText: "Continue",
              title: "Warning",
            }}
            size="60"
            classNames={"qes-button-secondary text-nowrap p-0 ms-3"}
            warnOnClose={this.state.displayOptionsDirty}
            testId={`${this.tableType}-providers-panel__viewable-fields-flyout-link`}
          >
            Display Options
          </FlyoutLink>
        )}
      </>
    );
  }

  private buildTable = (records: ServicingProvider[], displayedProviderType: DisplayedProviderType) => {
    const viewableFields = this.props.viewableFieldsByTableType.get(this.tableType);
    /* oxlint-disable sort-keys */
    const data: ImpactProviderTableDataWithCargo[] = records.map((provider) => {
      const providerCargoData: Partial<ImpactProviderTableDataWithCargo> = getCargoDataForProvider(provider, viewableFields, this.cargoColumns);
      const checkIcon = <QesIcon iconName={QesIcons.Check} isDecorative className="align-text-top" />;
      const xCloseIcon = <QesIcon iconName={QesIcons.XClose} isDecorative className="align-text-top" />;

      return {
        [wafflesKeyColumnName]: provider.providerId.toString(),
        name: restrictByFields(viewableFields, [NPF.NPI, NPF.DisplayName, NPF.TIN], (has) => (
          <>
            {has(NPF.DisplayName) && (
              <FlyoutLink
                classNames="p-0 qes-button-secondary"
                content={{
                  body: this.getProviderProfile(provider) as JSX.Element,
                  title: ACCESS_POINT_PROFILE_FLYOUT_TITLE
                }}
                testId={`impact-providers-panel__provider-detail--${provider.providerId}`}
              >
                <QesIcon iconName={getEntityIcon(provider.entityType)} isDecorative className="align-text-top me-3" />
                {provider.displayName}
              </FlyoutLink>
            )}
            {has(NPF.NPI) && !!provider.npi && (
              <span className="text-muted ml15 d-block">NPI: {provider.npi}</span>
            )}
            <RoleRestricted allowedRoles={[Roles.TIN_VIEWER]}>
              {has(NPF.TIN) && !!provider.tin && (
                <span className="text-muted ml15 d-block">TIN: {provider.tin}</span>
              )}
            </RoleRestricted>
          </>
        )),
        ...buildCommonImpactProviderColumns(provider, viewableFields, this.props.runConfiguration) as ImpactProviderTableData,
        recordingCriteria: <div>{(provider as ProjectionProvider).recordingCriteria}</div>,
        projectionReason: (() => {
          const projectionProvider = provider as ProjectionProvider;
          return (
            <>
              {!projectionProvider.processed && <div className="demibold">UNPROCESSED</div>}
              {displayedProviderType === "excluded" && (
                <>
                  {(this.props.user.roles.includes(Roles.PROVIDER_EXCLUDE_USER) && this.props.runConfiguration.isLatestPublished) && (
                    <TooltipWrapper text="Edit/View Excluded Provider Details" disableOnFocus={true}>
                      <QESButton
                        className="side-panel-link fw-normal text-info p-0"
                        onClick={e => {
                          (e.currentTarget as HTMLButtonElement).blur();
                          this.handleOpenEditProviderFlyout(projectionProvider, ProjectionProviderType.ExcludeImpact, true);
                        }}
                        qesTestId={`impact-providers-panel__exclusion-details--edit--${provider.providerId}`}
                        qesType="none"
                        qesRef={this.flyoutButtonRefs.current[provider.providerId]}
                      >
                        {projectionProvider.reasonName ?
                          <span title={projectionProvider.reasonName}>{projectionProvider.reasonName}</span> : "Other"}
                      </QESButton>
                    </TooltipWrapper>
                  )}
                  {(!this.props.user.roles.includes(Roles.PROVIDER_EXCLUDE_USER) || this.props.runConfiguration.datePublished == null) && (
                    <TooltipWrapper text="View Excluded Provider Details" disableOnFocus={true}>
                      <QESButton
                        className="side-panel-link fw-normal text-info p-0"
                        onClick={e => {
                          (e.currentTarget as HTMLButtonElement).blur();
                          this.handleOpenEditProviderFlyout(projectionProvider, ProjectionProviderType.ExcludeImpact, true);
                        }}
                        qesTestId={`impact-providers-panel__exclusion-details--no-edit--${provider.providerId}`}
                        qesType="none"
                        qesRef={this.flyoutButtonRefs.current[provider.providerId]}
                      >
                        {projectionProvider.reasonName ?
                          <span title={projectionProvider.reasonName}>{projectionProvider.reasonName}</span> : "Other"}
                      </QESButton>
                    </TooltipWrapper>
                  )}
                </>
              )
              }
              {displayedProviderType === "targeted" && (
                <>
                  {(this.props.user.roles.includes(Roles.PROVIDER_TARGET_USER) && this.props.runConfiguration.isLatestPublished) && (
                    <TooltipWrapper text="View/Edit Targeted Provider Details" disableOnFocus={true}>
                      <QESButton
                        className="side-panel-link fw-normal text-info p-0"
                        onClick={e => {
                          (e.currentTarget as HTMLButtonElement).blur();
                          this.handleOpenEditProviderFlyout(projectionProvider, ProjectionProviderType.Target, true);
                        }}
                        qesTestId={`impact-providers-panel__target-details--edit--${provider.providerId}`}
                        qesType="none"
                        qesRef={this.flyoutButtonRefs.current[provider.providerId]}
                      >
                        {projectionProvider.reasonName ? <span title={projectionProvider.reasonName}>{projectionProvider.reasonName}</span> : "Other"}
                      </QESButton>
                    </TooltipWrapper>
                  )}
                  {(!this.props.user.roles.includes(Roles.PROVIDER_TARGET_USER) || this.props.runConfiguration.datePublished == null) && (
                    <TooltipWrapper text="View Targeted Provider Details" disableOnFocus={true}>
                      <QESButton
                        className="side-panel-link fw-normal text-info p-0"
                        onClick={e => {
                          (e.currentTarget as HTMLButtonElement).blur();
                          this.handleOpenEditProviderFlyout(projectionProvider, ProjectionProviderType.Target, true);
                        }}
                        qesTestId={`impact-providers-panel__target-details--no-edit--${provider.providerId}`}
                        qesType="none"
                        qesRef={this.flyoutButtonRefs.current[provider.providerId]}
                      >
                        {projectionProvider.reasonName ? <span title={projectionProvider.reasonName}>{projectionProvider.reasonName}</span> : "Other"}
                      </QESButton>
                    </TooltipWrapper>
                  )}
                </>
              )
              }
              {(projectionProvider.allLocations || projectionProvider.allSpecialties || projectionProvider.allProjects) &&
                <>
                  {projectionProvider.allLocations && projectionProvider.allSpecialties && <><div>{displayedProviderType === "excluded" ? xCloseIcon : checkIcon}All Locations & Specialties</div></>}
                  {projectionProvider.allLocations && !projectionProvider.allSpecialties && <><div>{displayedProviderType === "excluded" ? xCloseIcon : checkIcon}All Locations</div></>}
                  {projectionProvider.allSpecialties && !projectionProvider.allLocations && <><div>{displayedProviderType === "excluded" ? xCloseIcon : checkIcon}All Specialties</div></>}
                  {displayedProviderType === "excluded" && projectionProvider.allProjects && <><div>{xCloseIcon}All Projects</div></>}
                </>
              }
            </>
          );
        })(),
        projectionReasonDescription: <div className="impact-providers__projection-reason">{this.ellipsifyDescription((provider as ProjectionProvider).reasonDescription)}</div>,
        lastUpdate: <div className="impact-providers__projection-last-update">{provider.processed && formatDateCustom((provider as ProjectionProvider).createDate, "MMM D 'YY hh:mm A")}</div>,
        lastUpdateBy: <div className="impact-providers__projection-last-update-by">{provider.processed && this.ellipsifyDescription((provider as ProjectionProvider).createUserDisplayName)}</div>,
        providerGrouping: provider.providerGrouping ? provider.providerGrouping : "---",
        providerValidations: (() => {
          const val = provider.providerValidations;
          if (!val || val === "---") return "---";
          const items = val.split(", ");
          if (items.length <= 3) return val;
          const truncated = `${items.slice(0, 3).join(", ")}…`;
          return <TooltipWrapper text={val} tooltipWidth="350px">{truncated}</TooltipWrapper>;
        })(),
        ...providerCargoData,
      };
    });
    /* oxlint-enable sort-keys */

    return data;
  }

  private ellipsifyDescription = (description: string) => {
    if (!description || description.length < 250) {
      return description;
    }
    return (
      <TooltipWrapper text={description} tooltipWidth="350px">
        {description.substring(0, 250)}...
      </TooltipWrapper>
    );
  }

  private customSort = (records: ImpactProviderTableDataWithCargo[], column: keyof ImpactProviderTableDataWithCargo, direction: SortDirections) => {
    this.buildServicingProviders(true, this.state.columnConfig[column].cargoTitle ? "CargoData" : column, direction, this.state.columnConfig[column].cargoTitle);
    return records;
  }

  private handleLoadMore = (justChecking: boolean) => {
    if (!justChecking) {
      if (this.state.displayedProviderType === "servicing") {
        this.buildServicingProviders(false, this.state.lastSortedColumn, this.state.lastSortedDirection, this.state.lastSortedCargo, false);
        return (this.state.offset + this.batchCount) < this.state.totalCount;
      } else {
        this.setState((prevState) => ({
          offset: prevState.offset + this.batchCount,
        }));
        const providerCount = this.state.displayedProviderType === "excluded" ? this.state.excludedRecords.length : this.state.targetedRecords.length;
        return (this.state.offset + this.batchCount) < providerCount;
      }
    }
    if (this.state.displayedProviderType === "servicing") {
      return this.state.offset < this.state.totalCount;
    } else {
      const providerCount = this.state.displayedProviderType === "excluded" ? this.state.excludedRecords.length : this.state.targetedRecords.length;
      return this.state.offset < providerCount;
    }
  }

  private onCheckboxChange = (newSelectedKeys: string[], isBulk?: boolean) => {
    const currentList = this.state.displayedProviderType === "servicing"
      ? this.state.sortedRecords
      : this.state.displayedProviderType === "excluded"
        ? this.state.excludedRecords
        : this.state.displayedProviderType === "targeted"
          ? this.state.targetedRecords
          : this.state.selectedProviders;

    let newSelectedProviders = [...this.state.selectedProviders];

    if (isBulk) {
      if (newSelectedKeys.length > 0) {
        newSelectedProviders.push(...currentList.filter((provider) => newSelectedKeys.includes(provider.providerId.toString())));
      } else {
        const removedIds = currentList.map((provider) => provider.providerId);
        newSelectedProviders = newSelectedProviders.filter((provider) => { return !removedIds.includes(provider.providerId); });
      }
    } else {
      if (newSelectedKeys.length > this.state.selectedProviders.length) {
        // one was added
        const addedKey = newSelectedKeys.filter((key) => !this.state.selectedProviders.find((provider) => provider.providerId.toString() === key))[0];
        // since the user clicked its box, it's safe to assume that provider is currently in memory
        const addedProvider = currentList.find((record) => record.providerId.toString() === addedKey);
        newSelectedProviders.push(addedProvider);
      } else if (newSelectedKeys.length < this.state.selectedProviders.length) {
        // one was removed
        const removedIndex = newSelectedProviders.findIndex((provider) => !newSelectedKeys.includes(provider.providerId.toString()));
        newSelectedProviders.splice(removedIndex, 1);
      }
    }
    // now we need to dedupe the array of objects...
    newSelectedProviders = newSelectedProviders.reduce((result, current) => {
      if (!result.find((r) => r.providerId === current.providerId)) {
        result.push(current);
      }
      return result;
    }, new Array<ServicingProvider>());

    this.setState({
      selectedProviders: newSelectedProviders,
    }, () => dismiss(this.toastId));
  }

  private getColumnConfig = (viewableFields: ViewableField[] = this.props.viewableFieldsByTableType.get(this.tableType)) => {
    return buildAdequacyProvidersColumnConfig(
      viewableFields,
      this.tableType,
      this.props.project.showNppesDates && this.props.runConfiguration && this.props.runConfiguration.showNppesDates,
      this.props.project.showMipsScore && this.props.runConfiguration && this.props.runConfiguration.showMipsScore,
      this.props.project.showPciData && this.props.runConfiguration && this.props.runConfiguration.showPciData,
      this.props.project.showVaClaimsVolumeColumn,
      this.props.project.showTricareClaimsVolumeColumn,
      undefined,
      undefined,
      this.state.displayedProviderType !== "servicing",
      false,
      false,
      this.state.displayedProviderType === "excluded",
      this.props.project.showLocationConfidenceScores && this.props.runConfiguration && this.props.runConfiguration.showLocationConfidenceScores,
      this.props.project.showMotiveAppropriatenessScore && this.props.runConfiguration && this.props.runConfiguration.showMotiveAppropriatenessScore,
      this.props.runConfiguration?.showCareJourneyScores,
      this.props.runConfiguration?.showAffiliationsInsights,
      undefined,
      this.props.enabledFeatureFlags.has(Features.CCIIO_DISPLAY_OPTION),
      this.props.enabledFeatureFlags.has(Features.CCIIO_DISPLAY_OPTION),
      this.isZipProject,
      this.props.enabledFeatureFlags.has(Features.NETWORK_PERFORMANCE_SPECIALTY_PREDICTION_MODEL),
      this.showPgrConfidenceTier()
    );
  }

  private showPgrConfidenceTier = (): boolean => {
    if (!this.props.runConfiguration || !this.props.runConfiguration.showPgrConfidenceTier) {
      return false;
    }

    return this.props.providerNetworkType === ProviderNetworkTypeEnum.Market
      || this.props.enabledFeatureFlags.has(Features.SHOW_CONFIDENCE_TIER_NON_MARKET_ACCESS_POINTS);
  }

  private excludedDisabledToolTip = (selectedProviders: ServicingProvider[]) => {
    if (this.props.user.roles.includes(Roles.TIN_VIEWER) && (selectedProviders.some((provider) => !provider.npi && provider.tin) && selectedProviders.some((provider) => provider.npi && !provider.tin))) {
      //if a mix of NPI only and TIN only is selected
      return 'All selected providers must have NPIs or all selected providers must have TINs.';
    }
    return '';
  }

  private targetedDisabledToolTip = (selectedProviders: ServicingProvider[]) => {
    // If matching on name is enabled, we allow providers to be targeted that are missing NPI and TIN
    const matchOnName = selectedProviders.some((provider) => !provider.npi && !provider.tin);
    if (this.props.user.roles.includes(Roles.TIN_VIEWER)) {
      //if a mix of NPI only and TIN only is selected
      if (selectedProviders.some((provider) => !provider.npi && provider.tin) && selectedProviders.some((provider) => provider.npi && !provider.tin)) {
        return "All selected providers must have NPIs or all selected providers must have TINs.";
      }
      //for targeting only, if any providers are missing NPI or TIN
      if (!matchOnName && selectedProviders.some((provider) => !provider.npi && !provider.tin)) {
        return "All selected providers must have NPIs or all selected providers must have TINs.";
      }
    } else {
      //for targeting only, if any providers have no NPI
      if (!matchOnName && selectedProviders.some((provider) => !provider.npi)) {
        return "All selected providers must have NPIs.";
      }
    }

    return "";
  }
}

const WrappedImpactProvidersPanel = withFlyoutContext(withProjectionProviderDetailsReducer(withZipContextState(withUserContext(withViewableFields(withCountyProjectContext(withFeatureFlagContext(withZipContextEventHandlers(ImpactProvidersPanel))))))));

const WrappedImpactProvidersPanelWithFlyoutProvider: React.FC<{
  accessSummary: AccessSummary
  className?: string
  exportEndpoint: string
  projectURL: string
  providerNetworkType: ProviderNetworkTypeEnum.Market | ProviderNetworkTypeEnum.Prospect
  regionSetId: number
  specialtyGroupCode: string
}> = (props) => {
  return (
    <FlyoutContextProvider>
      <WrappedImpactProvidersPanel {...props} />
    </FlyoutContextProvider>
  )
}

export { WrappedImpactProvidersPanelWithFlyoutProvider as ImpactProvidersPanel };
export { ImpactProvidersPanel as ImpactProvidersPanelClass };
